import React, { useState } from 'react';
import { Card, CardContent } from './card';
import { Edit2, Save, X, Tag, ExternalLink, EyeOff, Star } from 'lucide-react';
import { useStagingTray } from '../../context/StagingTrayContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useSkipCategories } from '../../context/SkipCategoriesContext';
import FavoriteStar from './FavoriteStar';
import SkipCategoriesButton from './SkipCategoriesButton';
import SkipCategoriesModal from './SkipCategoriesModal';
import TagManager from './TagManager';
import { InkQuoteApi } from '../../services/inkquoteApi';

const ManualQuoteCard = ({ quote, onUpdateQuote, viewMode = null }) => {
  const { addItem, removeItem, isInTray } = useStagingTray();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { isSkipped, getSkipCategories, SKIP_CATEGORY_LABELS } = useSkipCategories();
  const inTray = isInTray(quote);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(quote.content);
  const [editedAuthor, setEditedAuthor] = useState(quote.author);
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [quoteTags, setQuoteTags] = useState(quote.tags || []);
  const longPressTimer = React.useRef(null);
  const [isLongPressing, setIsLongPressing] = React.useState(false);
  const touchStart = React.useRef({ x: 0, y: 0, time: 0 });

  const quoteIsSkipped = isSkipped(quote.id);
  const skipCategories = getSkipCategories(quote.id);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleSave = async () => {
    try {
      await onUpdateQuote(quote._inkquote_id, {
        content: editedContent,
        author: editedAuthor
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save quote:', error);
    }
  };

  const handleCancel = () => {
    setEditedContent(quote.content);
    setEditedAuthor(quote.author);
    setIsEditing(false);
  };

  const handleLongPressStart = (e) => {
    // Skip if clicking interactive elements or not in manage mode
    if (e.target.closest('a, button, form, textarea, input') || viewMode !== 'manage' || isEditing) return;

    // Record initial touch position and time
    const touch = e.touches ? e.touches[0] : e;
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };

    setIsLongPressing(true);
    longPressTimer.current = setTimeout(() => {
      setShowActions(true);
      setIsLongPressing(false);
    }, 500); // 500ms for mobile long-press
  };

  const handleTouchMove = (e) => {
    // If there's an active long-press timer, check if we've moved enough to cancel it
    if (longPressTimer.current && touchStart.current) {
      const touch = e.touches ? e.touches[0] : e;
      const deltaX = Math.abs(touch.clientX - touchStart.current.x);
      const deltaY = Math.abs(touch.clientY - touchStart.current.y);

      // Cancel long-press if finger moved more than 10px (indicates scrolling)
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
        setIsLongPressing(false);
      }
    }
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setIsLongPressing(false);
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();

    // Check if this was a scroll gesture (touch moved more than 10px or was quick)
    if (touchStart.current) {
      const touch = e.changedTouches ? e.changedTouches[0] : e;
      const deltaX = Math.abs(touch.clientX - touchStart.current.x);
      const deltaY = Math.abs(touch.clientY - touchStart.current.y);
      const touchDuration = Date.now() - touchStart.current.time;

      // Ignore if finger moved more than 10px (was scrolling)
      if (deltaX > 10 || deltaY > 10) {
        return;
      }

      // Ignore if touch was very quick (< 50ms) as it's likely scroll momentum
      if (touchDuration < 50) {
        return;
      }
    }

    toggleFavorite(quote.id);
  };

  const handleCardClick = (e) => {
    if (e.target.closest('a, button, form, textarea, input') || isEditing || isLongPressing) {
      return;
    }
    // Only enable staging tray in Batch mode
    if (viewMode === 'batch') {
      inTray ? removeItem(quote) : addItem(quote);
    }
  };

  const handleSkip = () => {
    setIsSkipModalOpen(true);
    setShowActions(false); // Close action overlay when opening modal
  };

  const handleDelete = async () => {
    if (window.confirm('Delete this quote permanently?')) {
      try {
        await InkQuoteApi.deleteQuote(quote._inkquote_id);
        window.location.reload(); // Temporary - will improve in Priority 2
      } catch (error) {
        console.error('Failed to delete quote:', error);
        alert('Failed to delete quote. Please try again.');
      }
    }
  };

  const [showActions, setShowActions] = useState(false);

  return (
    <Card
      className={`transition-all duration-200 ${
        viewMode === 'batch' ? 'cursor-pointer' : ''
      } ${
        inTray && viewMode === 'batch'
          ? 'ring-2 ring-blue-500 dark:ring-blue-400 !bg-blue-50 dark:!bg-blue-900/20'
          : quoteIsSkipped
          ? 'opacity-60 bg-white dark:bg-gray-800 hover:shadow-md border-red-200 dark:border-red-900'
          : 'bg-white dark:bg-gray-800 hover:shadow-md hover:!bg-gray-50 dark:hover:!bg-gray-700/50'
      }`}
      onClick={handleCardClick}
      onTouchStart={handleLongPressStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleLongPressEnd}
      onMouseDown={handleLongPressStart}
      onMouseMove={handleTouchMove}
      onMouseUp={handleLongPressEnd}
      onMouseEnter={() => viewMode === 'manage' && setShowActions(true)}
      onMouseLeave={(e) => {
        handleLongPressEnd(e);
        if (viewMode === 'manage') setShowActions(false);
      }}
      data-quote-id={quote.id}
    >
      <CardContent className="p-4 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <EyeOff size={18} className="text-purple-600 dark:text-purple-400" />
            {isEditing ? (
              <input
                type="text"
                value={editedAuthor}
                onChange={(e) => setEditedAuthor(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="font-semibold px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600"
              />
            ) : (
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {quote.author}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formatDate(quote.created_at)}
            </span>
            <FavoriteStar itemId={quote.id} size={16} inline />
          </div>
        </div>

        {/* Skip Categories Badges */}
        {quoteIsSkipped && skipCategories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {skipCategories.map(category => (
              <span
                key={category}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                  bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400
                  border border-red-200 dark:border-red-800"
              >
                {SKIP_CATEGORY_LABELS[category]}
              </span>
            ))}
          </div>
        )}

        {/* Content */}
        {isEditing ? (
          <div className="mb-3" onClick={e => e.stopPropagation()}>
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              rows={4}
            />

            {/* Dev Info */}
            <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  ID: <span className="text-gray-900 dark:text-white">{quote._inkquote_id}</span>
                </span>
                <a
                  href={`https://api.treasurechest.design/inkquote/quotes/${quote._inkquote_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink size={12} />
                  API Endpoint
                </a>
              </div>
            </div>

            <div className="mt-2 flex gap-2">
              <button
                onClick={handleSave}
                className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1"
              >
                <Save size={16} /> Save
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <p
            className={`text-gray-900 dark:text-gray-100 leading-relaxed whitespace-pre-wrap mb-3 ${viewMode !== null ? 'px-2 py-1 -mx-2 -my-1 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors' : ''}`}
            onClick={viewMode !== null && !isEditing ? (e) => {
              e.stopPropagation();
              setIsEditing(true);
            } : undefined}
            title={viewMode !== null ? "Click to edit content" : undefined}
          >
            {quote.content}
          </p>
        )}

        {/* Tags */}
        {quoteTags && quoteTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {quoteTags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                  bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Bottom Actions - Only in InkQuote tab */}
        {viewMode !== null && (
          <div className="flex justify-end items-center gap-2 mt-2">
            {/* Favorite Star Button */}
            <button
              onTouchStart={(e) => {
                const touch = e.touches[0];
                touchStart.current = {
                  x: touch.clientX,
                  y: touch.clientY,
                  time: Date.now()
                };
              }}
              onClick={handleFavoriteClick}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Toggle Favorite"
            >
              <Star
                size={18}
                className={isFavorite(quote.id) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600 dark:text-gray-400'}
              />
            </button>

            {/* Tag and Skip buttons */}
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsTagModalOpen(true);
                }}
                className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Manage Tags"
              >
                <Tag size={18} />
              </button>

              <SkipCategoriesButton tweetId={quote.id} tweetSource="inkquote_manual" />
            </>
          </div>
        )}

      </CardContent>

      {/* Skip Categories Modal */}
      {isSkipModalOpen && (
        <SkipCategoriesModal
          tweetId={quote.id}
          tweetSource="inkquote_manual"
          isOpen={isSkipModalOpen}
          onClose={() => setIsSkipModalOpen(false)}
        />
      )}

      {/* Tag Manager Modal */}
      {isTagModalOpen && (
        <TagManager
          quoteId={quote._inkquote_id}
          currentTags={quoteTags}
          isOpen={isTagModalOpen}
          onClose={() => setIsTagModalOpen(false)}
          onTagsUpdated={(newTags) => {
            setQuoteTags(newTags);
            // Optionally reload the quote to get fresh data
            if (onUpdateQuote) {
              onUpdateQuote(quote._inkquote_id, {});
            }
          }}
        />
      )}
    </Card>
  );
};

export default ManualQuoteCard;
